import React, { useCallback, useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Books, FloppyDisk, ChartLineUp, GraduationCap } from "@phosphor-icons/react";

const STATUS_STYLE = {
  in_review: "border-[var(--vs-info)]/40 text-[var(--vs-info)] bg-[var(--vs-info)]/10",
  draft: "border-[var(--vs-warning)]/40 text-[var(--vs-warning)] bg-[var(--vs-warning)]/10",
  published: "border-[var(--vs-success)]/40 text-[var(--vs-success)] bg-[var(--vs-success)]/10",
};

export default function TeacherMarks() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);

  const defaultExam = `Annual ${new Date().getFullYear()}`;
  const [examName, setExamName] = useState(defaultExam);
  const [academicYear, setAcademicYear] = useState(String(new Date().getFullYear()));
  const [maxMarks, setMaxMarks] = useState(100);
  const [classFilter, setClassFilter] = useState("");
  const [data, setData] = useState(null); // { subject, class_name, students: [...] }
  const [edits, setEdits] = useState({}); // student_id -> obtained
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/teacher/profile").then((r) => {
      setProfile(r.data);
      const cls = r.data.classes_assigned?.[0];
      if (cls) setClassFilter(cls);
    });
  }, []);

  const refresh = useCallback(async () => {
    if (!classFilter) return;
    setLoading(true);
    setEdits({});
    try {
      const r = await api.get("/teacher/marks", {
        params: { exam_name: examName, academic_year: academicYear, class_name: classFilter },
      });
      setData(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [classFilter, examName, academicYear]);

  useEffect(() => {
    if (classFilter) refresh();
  }, [classFilter, refresh]);

  const setMark = (sid, value) => {
    const cleaned = value === "" ? "" : value.replace(/[^\d]/g, "");
    setEdits((m) => ({ ...m, [sid]: cleaned }));
  };

  const submit = async () => {
    if (!data) return;
    const entries = Object.entries(edits)
      .filter(([_, v]) => v !== "" && v !== undefined && v !== null)
      .map(([student_id, v]) => ({ student_id, obtained: Number(v) }));
    if (entries.length === 0) {
      toast.info("No new marks entered. Make a change before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/teacher/marks", {
        exam_name: examName,
        academic_year: academicYear,
        class_name: classFilter,
        max_marks: Number(maxMarks),
        entries,
      });
      const { created, updated, errors } = res.data;
      toast.success(`Submitted — ${created} new, ${updated} updated${errors?.length ? `, ${errors.length} errors` : ""}`);
      if (errors?.length) {
        logger.warn("bulk marks errors", errors);
      }
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const summary = useMemo(() => {
    if (!data) return null;
    const total = data.students.length;
    const entered = data.students.filter((s) => s.obtained !== null && s.obtained !== undefined).length;
    return { total, entered, pending: total - entered };
  }, [data]);

  const dirtyCount = Object.values(edits).filter((v) => v !== "" && v !== undefined && v !== null).length;

  if (!profile) {
    return <DashboardLayout title="Loading…"><div className="text-sm text-[var(--vs-text-secondary)]">Fetching teacher profile…</div></DashboardLayout>;
  }

  return (
    <DashboardLayout
      title={`${profile.subject || "Subject"} — Marks Entry`}
      subtitle={`Subject-wise bulk entry across all your assigned classes (${(profile.classes_assigned || []).map(c => `Class ${c}`).join(", ") || "none"}).`}
    >
      {/* Filters */}
      <div className="vs-card p-5 grid grid-cols-1 md:grid-cols-5 gap-3 items-end mb-4" data-testid="teacher-marks-filters">
        <div>
          <Label className="text-[11px] uppercase tracking-widest font-mono text-[var(--vs-secondary)]">Class</Label>
          <Select value={classFilter} onValueChange={setClassFilter}>
            <SelectTrigger data-testid="tm-class-select" className="bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(profile.classes_assigned || []).map((c) => (
                <SelectItem key={c} value={c}>Class {c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest font-mono text-[var(--vs-secondary)]">Exam</Label>
          <Input data-testid="tm-exam-input" value={examName} onChange={(e) => setExamName(e.target.value)} className="bg-white" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest font-mono text-[var(--vs-secondary)]">Academic year</Label>
          <Input data-testid="tm-year-input" value={academicYear} onChange={(e) => setAcademicYear(e.target.value)} className="bg-white font-mono" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest font-mono text-[var(--vs-secondary)]">Max marks</Label>
          <Input data-testid="tm-max-input" type="number" value={maxMarks} onChange={(e) => setMaxMarks(e.target.value)} className="bg-white font-mono" />
        </div>
        <div className="flex gap-2">
          <Button data-testid="tm-load-btn" onClick={refresh} variant="outline" className="flex-1">Reload</Button>
          <Button
            data-testid="tm-submit-btn"
            onClick={submit}
            disabled={submitting || dirtyCount === 0}
            className="bg-[var(--vs-secondary)] hover:bg-[var(--vs-secondary-hover)] text-[var(--vs-bg)] flex-1"
          >
            <FloppyDisk size={14} className="mr-1.5" />
            {submitting ? "Saving…" : `Save ${dirtyCount > 0 ? `(${dirtyCount})` : ""}`}
          </Button>
        </div>
      </div>

      {/* Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--vs-border)] border border-[var(--vs-border)] rounded-md overflow-hidden mb-4">
          <Stat icon={Books} label="Subject" value={profile.subject} />
          <Stat icon={GraduationCap} label="Class" value={`Class ${classFilter}`} sub={`${summary.total} students`} />
          <Stat icon={ChartLineUp} label="Marks entered" value={summary.entered} sub={`${summary.pending} pending`} />
          <Stat label="Status" value={dirtyCount > 0 ? "Unsaved changes" : "All saved"} sub={dirtyCount > 0 ? `${dirtyCount} pending submit` : ""} />
        </div>
      )}

      {/* Bulk entry table */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="vs-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--vs-bg-subtle)]">
            <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
              <th className="px-4 py-3 w-16">Roll</th>
              <th className="px-4 py-3">VS-ID</th>
              <th className="px-4 py-3">Student name</th>
              <th className="px-4 py-3">Class · Sec</th>
              <th className="px-4 py-3">Current</th>
              <th className="px-4 py-3 w-40">New marks ({profile.subject})</th>
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-10 text-center text-[var(--vs-text-secondary)]">Loading…</td></tr>}
            {!loading && data?.students?.map((s) => (
              <tr key={s.student_id} data-testid={`teacher-mark-row-${s.vs_id}`} className="border-t border-[var(--vs-border)] hover:bg-[var(--vs-bg-subtle)]/40">
                <td className="px-4 py-3 font-mono text-xs">{s.roll_number || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs">{s.vs_id}</td>
                <td className="px-4 py-3 font-medium">{s.name}</td>
                <td className="px-4 py-3 text-xs">{s.class_name}-{s.section || ""}</td>
                <td className="px-4 py-3 font-mono">{s.obtained === null || s.obtained === undefined ? "—" : `${s.obtained}/${s.max}`}</td>
                <td className="px-4 py-3">
                  <Input
                    data-testid={`teacher-mark-input-${s.vs_id}`}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={edits[s.student_id] ?? ""}
                    onChange={(e) => setMark(s.student_id, e.target.value)}
                    placeholder={s.obtained !== null && s.obtained !== undefined ? String(s.obtained) : `0-${maxMarks}`}
                    className="bg-white font-mono w-28"
                  />
                </td>
                <td className="px-4 py-3">
                  {s.marksheet_status ? (
                    <Badge variant="outline" className={STATUS_STYLE[s.marksheet_status] || ""}>
                      {s.marksheet_status.replace("_", " ")}
                    </Badge>
                  ) : (
                    <span className="text-xs text-[var(--vs-text-secondary)]">No marksheet yet</span>
                  )}
                </td>
              </tr>
            ))}
            {!loading && data?.students?.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-[var(--vs-text-secondary)]">
                No active students in Class {classFilter}.
              </td></tr>
            )}
          </tbody>
        </table>
      </motion.div>

      <p className="mt-4 text-[11px] font-mono text-[var(--vs-text-secondary)]">
        Submitting marks creates or updates the student's marksheet for this exam and puts it in <strong>in_review</strong>.
        Only the school admin can publish to donors.
      </p>
    </DashboardLayout>
  );
}

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-[var(--vs-bg)] px-5 py-4">
      <div className="eyebrow flex items-center gap-1.5">
        {Icon && <Icon size={11} weight="duotone" />}
        {label}
      </div>
      <div className="font-display text-xl font-semibold mt-1">{value}</div>
      {sub && <div className="text-xs text-[var(--vs-text-secondary)] mt-0.5">{sub}</div>}
    </div>
  );
}
